import axios from 'axios';

// Create axios instance with timeout
const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000, // 30 second default timeout
});

// Add request interceptor to add auth token and log slow requests
api.interceptors.request.use(
  (config) => {
    config.metadata = { startTime: new Date() };

    // Add Authorization header if token exists
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to log slow responses
api.interceptors.response.use(
  (response) => {
    if (response.config.metadata) {
      const endTime = new Date();
      const duration = endTime - response.config.metadata.startTime;
      if (duration > 5000) {
        console.warn(`Slow API call to ${response.config.url}: ${duration}ms`);
      }
    }
    return response;
  },
  (error) => {
    if (error.config?.metadata) {
      const endTime = new Date();
      const duration = endTime - error.config.metadata.startTime;
      console.error(`Failed API call to ${error.config.url}: ${duration}ms`, error.message);
    }
    return Promise.reject(error);
  }
);

// ──────────────────────────────────────────────────────────────────────────────
// Auth
// ──────────────────────────────────────────────────────────────────────────────

export const loginUser = async (email, password, token = null) => {
  if (token) {
    // Use the token to fetch user details
    return api.post('/login', { token }).then((res) => res.data);
  } else {
    // Normal login flow using email and password
    return api.post('/login', { email, password }).then((res) => res.data);
  }
};

export const logoutUser = (token) => api.post('/logout', { token }).then((res) => res.data);

export const checkCreator = (wp_id) =>
  api.post('/creator/checkCreator', { wp_id }).then((res) => res.data);

// ──────────────────────────────────────────────────────────────────────────────
// Draft Rankings (shared table with web-app; server attributes the session user)
// ──────────────────────────────────────────────────────────────────────────────

export const getDfcDraftRankings = async (wp_id, source) => {
  return api.post('/getDfcDraftRankings', { wp_id, source }).then((res) => res.data);
};

export const editNiceUsername = async (wp_id, username) => {
  return api.post('/editNiceUsername', { wp_id, username }).then((res) => res.data);
};

export const submitDraftRankings = async (wp_id, source, rankings) => {
  return api.post('/submitDraftRankings', { wp_id, source, rankings }).then((res) => res.data);
};

// ──────────────────────────────────────────────────────────────────────────────
// Predicted Lineups (server scopes rows to the session user)
// ──────────────────────────────────────────────────────────────────────────────

export const getLineup = async (gw) => {
  return api.post('/getLineups', { gw }).then((res) => res.data);
};

export const setLineup = async (selectedPlayers, gw, lineupCommentary = []) => {
  return api.post('/setLineups', { selectedPlayers, gw, lineupCommentary }).then((res) => res.data);
};

export const getPrevLineup = async (gw) => {
  return api.post('/getPreviousLineups', { gw }).then((res) => res.data);
};

// ──────────────────────────────────────────────────────────────────────────────
// Blog (creator-scoped: own posts only, draft → pending_review workflow)
// ──────────────────────────────────────────────────────────────────────────────

export const getMyBlogPosts = (filters = {}) =>
  api.post('/creator/getMyBlogPosts', filters).then((res) => res.data);

export const getMyBlogPost = (id) =>
  api.post('/creator/getMyBlogPost', { id }).then((res) => res.data);

export const createBlogPost = (data) =>
  api.post('/creator/createBlogPost', data).then((res) => res.data);

export const updateBlogPost = (data) =>
  api.post('/creator/updateBlogPost', data).then((res) => res.data);

export const deleteBlogPost = (id) =>
  api.post('/creator/deleteBlogPost', { id }).then((res) => res.data);

export const getBlogCategories = () =>
  api.post('/creator/getBlogCategories', {}).then((res) => res.data);

export const uploadBlogImage = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/creator/uploadBlogImage', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((res) => res.data);
};

// ──────────────────────────────────────────────────────────────────────────────
// Referrals
// ──────────────────────────────────────────────────────────────────────────────

export const getReferralStatus = () =>
  api.post('/referrals/me', {}).then((res) => res.data);

export const generateReferralCode = (slug = null) =>
  api.post('/referrals/generateCode', slug ? { slug } : {}).then((res) => res.data);

export const connectOnboard = (country = null) =>
  api.post('/referrals/connect/onboard', country ? { country } : {}).then((res) => res.data);

export const connectCountries = () =>
  api.post('/referrals/connect/countries', {}).then((res) => res.data);

export const connectRefresh = () =>
  api.post('/referrals/connect/refresh', {}).then((res) => res.data);

export const uploadCreatorLogo = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/referrals/uploadCreatorLogo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((res) => res.data);
};

export const updateSocialLinks = (links) =>
  api.post('/referrals/updateSocialLinks', links).then((res) => res.data);

export const getCreatorBranding = (wp_id) =>
  api.post('/getCreatorBranding', { wp_id }).then((res) => res.data);

export default api;
